import { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compactModeHeaderBackground } from '../customStyle';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import {
    selectVideoWallLaneReceivedAt,
    selectVideoWallPinnedLaneUsername,
    selectVideoWallPinnedLaneTimestamp,
} from 'store/transactionsSlice/transactionsSelectors';
import { VIDEO_WALL_NOTIFICATION_FADE_MS } from 'store/transactionsSlice/transactionConstants';

import AIDetection from './AIDetection/AIDetection';
import Intercom from './Intercom/Intercom';
import AuxiliaryIndicator from './AuxiliaryIndicator/AuxiliaryIndicator';
import LanePinIndicator from 'components/transaction/components/panels/transaction/stickyLanes/LanePinIndicator';
import TransactionAssignmentActions from './TransactionAssignmentActions';
import DefaultCompactHeader from './DefaultCompactHeader/DefaultCompactHeader';
import CameraUnassignedInfo from './CameraUnassignedInfo';
import TransactionDuration from './TransactionDuration';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { Tooltip } from '@mui/material';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import ExpandIcon from '@mui/icons-material/Expand';
import CompressIcon from '@mui/icons-material/Compress';

import PropTypes from 'prop-types';

import styles from './CameraStreamCompactHeader.module.css';

const CameraStreamCompactHeader = ({
    show = true,
    camInfo,
    listeners = {},
    attributes = {},
    isHover = false,
    isClicked = false,
    assignee = '',
    cameraHasTransaction = false,
    isActiveTransaction = false,
    transactionStartTimestamp = '',
    showHeaderActions = false,
    activeUser = '',
    isNewCamera = false,
    hasHeaderCameraWindow = false,
    isTransactionUnassigned = false,
    isTransactionFadingOut = false,
    panelType = '',
    moveCameraBetweenPanels = () => {},
    isIntercomActive = false,
    hasIntercom = false,
    isDetectionOn,
}) => {
    const dispatch = useDispatch();
    const { locationName, gateName, direction, auxiliaryCamera: isAuxiliary = false } = camInfo;
    const pinnedByUsername = useSelector(selectVideoWallPinnedLaneUsername(camInfo.gateId));
    const pinnedAtTimestamp = useSelector(selectVideoWallPinnedLaneTimestamp(camInfo.gateId));
    const laneReceivedAt = useSelector(selectVideoWallLaneReceivedAt(camInfo.gateId));
    const showTransactionDetails = isActiveTransaction;
    const displayName = pinnedByUsername ?? (showTransactionDetails ? assignee : '');
    const effectiveStartTimestamp = pinnedAtTimestamp || transactionStartTimestamp;
    const isDraggableArea = listeners && Object.keys(listeners).length > 0;
    const isDirectionIn = direction === 'IN';
    const isSideBarPanel = panelType === 'sidebar';
    const panelDirection = panelType === 'sidebar' ? 'toMain' : 'toSidebar';
    const [isHoveredSidebarItem, setIsHoveredSidebarItem] = useState(false);
    const iconsColor = !isSideBarPanel ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)';
    const hasAssigned = Boolean(assignee) || !isHoveredSidebarItem;
    const canAssignUnassignedLane = isTransactionUnassigned && !isTransactionFadingOut;

    const draggableAreaStyle = {
        zIndex: isDraggableArea ? 1 : 'auto',
        cursor: isDraggableArea ? 'grab' : hasHeaderCameraWindow ? 'move' : 'grab',
        background: compactModeHeaderBackground({
            assignee,
            activeUser,
            hasTransaction: cameraHasTransaction,
            isSideBarPanel,
            isTransactionUnassigned,
            isRecentlyFinished: isTransactionFadingOut,
        }).background,
        color: !isSideBarPanel ? '#fff' : '#00000099',
        position: hasHeaderCameraWindow ? 'relative' : 'absolute',
        '--video-wall-notification-fade-duration': `${VIDEO_WALL_NOTIFICATION_FADE_MS}ms`,
    };

    const handleOpenInNewTab = () => {
        // A dialog-collapsed sidebar card has no CameraStream props to hand the popup window, so there is
        // nothing to open — skip instead of opening a window that can't render (and would crash on missing props).
        if (!camInfo?.cameraStreamComponentProps) return;
        dispatch(transactionActions.setCameraStreamWindow({ isOpen: true, camInfo: { ...camInfo, openSource: 'camera_stream_window' } }));
    };

    if (!show) return null;

    if (!isHover && !showTransactionDetails && !isTransactionUnassigned && !pinnedByUsername) {
        return (
            <DefaultCompactHeader
                auxiliaryElement={isAuxiliary ? <AuxiliaryIndicator show /> : null}
                aiDetectionElement={<AIDetection isDetectionOn={isDetectionOn} />}
                intercomElement={<Intercom isIntercomActive={isIntercomActive} show={hasIntercom} />}
                realTimeUpdateElement={
                    isNewCamera ? (
                        <Chip
                            label="New"
                            sx={{
                                background: 'var(--color-blue-400)',
                                fontSize: '10px',
                                color: '#fff',
                                lineHeight: '20px',
                                letterSpacing: '0.14px',
                                padding: '0',
                                margin: '0',
                                height: '20px',
                            }}
                        />
                    ) : (
                        <></>
                    )
                }
                locationName={locationName}
                gateName={gateName}
                direction={direction}
            />
        );
    }

    const transactionAssignmentButton = (
        <TransactionAssignmentActions
            cameraId={camInfo.gateCameraId}
            hasTransaction={showTransactionDetails}
            assignee={assignee}
            assignOnClick={canAssignUnassignedLane}
            unassignedSince={canAssignUnassignedLane ? laneReceivedAt : null}
            className={styles['assign-to-me-button--wrapper']}
        />
    );
    const showTransactionAssignement = (showTransactionDetails && (!assignee || assignee === activeUser)) || canAssignUnassignedLane;
    const shouldShowTransactionSection = showTransactionDetails || isTransactionUnassigned || !!pinnedByUsername;

    return (
        <div
            className={`${styles['camera-stream-compact-header--wrapper']} ${isTransactionFadingOut ? styles['camera-stream-compact-header--recently-finished'] : ''} ${isSideBarPanel && styles['camera-stream-compact-header-side--wrapper']} ${isSideBarPanel && isActiveTransaction && styles['camera-stream-compact-header-side-wrapper--pulse']}`}
            style={draggableAreaStyle}
            onMouseEnter={() => {
                if (isSideBarPanel) setIsHoveredSidebarItem(true);
            }}
            onMouseLeave={() => {
                if (isSideBarPanel) setIsHoveredSidebarItem(false);
            }}
            onDoubleClick={handleOpenInNewTab}
            {...listeners}
            {...attributes}
        >
            <div className={styles['camera-stream-compact-header--row']}>
                <div className={styles['camera-stream-compact-header--location']}>{locationName}</div>
                <CameraUnassignedInfo isUnassigned={isTransactionUnassigned && !isTransactionFadingOut} />
                <AIDetection isDetectionOn={isDetectionOn} isSideBarPanel={isSideBarPanel} />
                <Intercom isGreyIcon={isSideBarPanel} isIntercomActive={isIntercomActive} show={hasIntercom} />
                <LanePinIndicator show={!!pinnedByUsername} username={pinnedByUsername} color="white" />
                {isNewCamera && (
                    <Chip
                        label="New"
                        sx={{
                            background: 'var(--color-blue-400)',
                            fontSize: '12px',
                            color: '#fff',
                            lineHeight: '20px',
                            letterSpacing: '0.14px',
                            padding: '0',
                            margin: '0',
                            height: '20px',
                        }}
                    />
                )}
                <AuxiliaryIndicator show={isAuxiliary} />
                <div
                    className={`${styles['header-actions--wrapper']}${hasAssigned ? ` ${styles['header-actions--wrapper--collapsed']}` : ''}${showHeaderActions ? ` ${styles['header-actions--wrapper--hovered']}` : ''}`}
                >
                    <Tooltip title="Open in popup" placement="top">
                        <IconButton onClick={handleOpenInNewTab} size="small" aria-label="new-tab" sx={{ height: '1rem', marginBottom: '0.1rem' }}>
                            <OpenInBrowserIcon sx={{ width: '1.125rem', height: '1.125rem', color: iconsColor }} />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <div className={styles['camera-stream-compact-header--row']}>
                <div className={styles['camera-stream-compact-header--gate-info']} {...listeners} {...attributes}>
                    <span style={{ color: isDirectionIn ? '#66BB6A' : '#FFA726', marginRight: '0.1rem' }}>{direction}</span> |{' '}
                    <span style={{ marginLeft: '0.1rem' }}>{gateName}</span>
                </div>
                <div className={styles['header-actions--section']}>
                    {shouldShowTransactionSection ? (
                        <div className={styles['camera-stream-compact-header-assignee--info']}>
                            <span className={styles['camera-stream-compact-header-assignee--name']}>{displayName}</span>
                            {showTransactionAssignement && transactionAssignmentButton}
                            {showTransactionDetails && <TransactionDuration transactionStartTimestamp={effectiveStartTimestamp} />}
                        </div>
                    ) : (
                        <div
                            style={{ display: (!showHeaderActions || !isHoveredSidebarItem) && !isClicked && isSideBarPanel ? 'none' : 'flex' }}
                            className={styles['camera-stream-compact-header--gate-actions']}
                        >
                            {transactionAssignmentButton}
                        </div>
                    )}
                    <div
                        className={`${styles['header-actions--wrapper']}${hasAssigned ? ` ${styles['header-actions--wrapper--collapsed']}` : ''}${showHeaderActions ? ` ${styles['header-actions--wrapper--hovered']}` : ''}`}
                    >
                        <Tooltip title={isSideBarPanel ? 'Expand to grid' : 'Collapse to sidebar'}>
                            <IconButton onClick={() => moveCameraBetweenPanels(camInfo.id, panelDirection)} size="small" aria-label="collapse">
                                {isSideBarPanel ? (
                                    <ExpandIcon sx={{ width: '1rem', height: '1rem', color: iconsColor }} />
                                ) : (
                                    <CompressIcon sx={{ width: '1rem', height: '1rem', color: iconsColor }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};

CameraStreamCompactHeader.propTypes = {
    show: PropTypes.bool,
    camInfo: PropTypes.shape({
        locationName: PropTypes.string.isRequired,
        gateName: PropTypes.string.isRequired,
        direction: PropTypes.string.isRequired,
    }).isRequired,
    listeners: PropTypes.object,
    attributes: PropTypes.object,
    isHover: PropTypes.bool,
    isClicked: PropTypes.bool,
    assignee: PropTypes.string,
    cameraHasTransaction: PropTypes.bool,
    isActiveTransaction: PropTypes.bool,
    transactionStartTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showHeaderActions: PropTypes.bool,
    activeUser: PropTypes.string,
    isNewCamera: PropTypes.bool,
    hasHeaderCameraWindow: PropTypes.bool,
    isTransactionUnassigned: PropTypes.bool,
    isTransactionFadingOut: PropTypes.bool,
    moveCameraBetweenPanels: PropTypes.func,
    panelType: PropTypes.string,
    isIntercomActive: PropTypes.bool,
    hasIntercom: PropTypes.bool,
    isDetectionOn: PropTypes.bool,
};

export default memo(CameraStreamCompactHeader);
